import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'

import { interpolate as d3_interpolate, interpolateRgb as d3_interpolateRgb } from 'd3-interpolate'
import { arc as d3_arc } from 'd3-shape'
import { select as d3_select } from 'd3-selection'
import SizeHelper from '../chart_helpers/SizeHelper'
import { wrapSvgText, ellipseSvgText } from '../utils/textUtils'
import { formatNumber } from '../utils/formatUtils'
import { pieChartLabels } from '../utils/labelUtils'
import { ACCESSIBILITY_OPTIONS_KEYS } from '../utils/accessibilityUtils'

export default class PieChartChart extends BaseChart {
  constructor(...args) {
    super(...args)
  }
  static defaultConfig = {
    mode: {
      type: String,
      default: 'pie',
      options: ['pie', 'donut'],
      exposed: false,
    },
    shape: {
      type: String,
      default: 'full',
      options: ['full', 'half'],
      exposed: true,
    },
    dataSorting: {
      type: [String],
      default: 'none',
      options: ['none', 'asc', 'desc'],
      exposed: true,
    },
    labelValue: {
      type: [String],
      default: 'none',
      options: ['none', 'abs', 'percent'],
      exposed: true,
    },
    labelOffsets: {
      type: [Object, null],
      default: null,
      exposed: true,
    },
    /**
     * {-EN-} If set, all values smaller than this threshold will be grouped in a single sector labeled "Others" by default.
     * {-ES-} Si se establece, todos los valores inferiores a este umbral se agruparán en un único sector denominado "Otros" por defecto.
     */
    groupOthersThreshold: {
      type: [Number, null],
      default: null,
      exposed: true,
    },
    /**
     * {-EN-} Customize the label of grouped values using the `groupOthersThreshold` prop.
     * {-ES-} Personaliza la etiqueta de los valores agrupados utilizando la propiedad groupOthersThreshold.
     */
    groupOthersLabel: {
      type: String,
      default: 'Others',
      exposed: true,
    },

    /**
     * {-EN-} If set to `true`, sector values will be show as a percentage of the total.
     * {-ES-} Si se establece en `true`, los valores del sector se mostrarán como un porcentaje del total.
     */
    showPercentage: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    /**
     * {-EN-} Format for the percentages.
     * {-ES-} Formato para los porcentajes.
     */
    percentageFormat: {
      type: [String],
      default: '[N]0,0.0',
      exposed: true,
    },
    /**
     * {-EN-} If a value is set, it will be displayed in the center of the donut. If set to `true`, the total sum of the sector values will be displayed.
     * {-ES-} Si se establece un valor, se mostrará en el centro del donut. Si se establece en `true`, se mostrará la suma total de los valores del sector.
     */
    centerValue: {
      type: [Boolean, Number, String, Object],
      default: true,
      exposed: true,
    },
    /**
     * {-EN-} If a value is set to true, the selected item value will be displayed in the center of the donut
     * {-ES-} Si un valor se establece en verdadero, el valor del elemento seleccionado se mostrará en el centro del donut.
     */
    selectedItemCenterValue: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    /**
     * {-EN-} If a value is set, it will be displayed in the above the central value of the donut.
     * {-ES-} Si se establece un valor, se mostrará arriba el valor central del donut.
     */
    centerTopText: {
      type: [String],
      // default: '<div><b>BOLD</b>&<i>ITALIC</i></div>',
      default: '',
      exposed: true,
    },
    /**
     * {-EN-} If a value is set, it will be displayed in the under the central value of the donut.
     * {-ES-} Si se establece un valor, se mostrará debajo del valor central del donut.
     */
    centerBottomText: {
      type: [String],
      // default: `<a href="https://www.bancosantander.es" target="_blank">link 1</a><span style="color: #f0f; text-decoration: underline; cursor: pointer" onclick="(function(){
      //   alert('Test Alert!');
      // })();">js function</span>`,
      default: '',
      exposed: true,
    },
    /**
     * {-EN-} If set, all sectors will span at least this angle. Useful if some values are too small to be seen.
     * {-ES-} Si se establece, todos los sectores abarcarán al menos este ángulo. Útil si algunos valores son demasiado pequeños para verse.
     */
    minSectorAngle: {
      type: Number,
      default: 0,
      exposed: true,
    },
    /**
     * {-EN-} Thickness of the donut
     * {-ES-} Grosor del donut
     */
    donutThickness: {
      type: String,
      default: 'medium',
      options: ['thick', 'medium', 'thin'],
      exposed: true,
    },
    /**
     * {-EN-} Show/hide labels for each sector.
     * {-ES-} Mostrar/ocultar etiquetas para cada sector.
     */
    showLabels: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    /**
     * {-EN-} Show/hide icon for each sector label.
     * {-ES-} Mostrar/ocultar icono para cada etiqueta de sector.
     */
    showLabelIcons: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    showLabelCategories: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    hasColorPatterns: {
      type: Boolean,
      default: true,
      exposed: false,
    },
    TooltipBehavior: {
      getHtml: function (d) {
        //Chapuzilla porque no esta en ningun axis, y no nos vale "formatValue(value,axis)"
        let formatter = (value, format) => {
          let match = this.matchFormatString(format || '')
          let numberFormat = match && match[2]
          let thouDel = this.config.thousandsDelimiter
          let decDel = this.config.decimalDelimiter
          return formatNumber(value, numberFormat, thouDel, decDel)
        }
        if (d.data.othersData) {
          let datas = d.data.othersData
          let html = datas.map(dd => {
            return `
            <div class="tip-value important">${this.formatTooltipValue(dd.value)}</div>
            <div class="tip-id">
              <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
                { id: dd.id }
              )}; margin-right: 3px"></span>
              ${dd.id}
            </div>
            ${
              this.config.showPercentage
                ? '<div class="secondary">' +
                  formatter(dd.percent, this.config.percentageFormat) +
                  '%</div>'
                : ''
            }
            `
          })
          return html.join('<br/>')
        } else {
          return `
          <div class="tip-value important">${this.formatTooltipValue(d.data.value)}</div>
          <div class="tip-id">
            <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
              { id: d.id }
            )}; margin-right: 3px"></span>
            ${d.id}
          </div>
          ${
            this.config.showPercentage
              ? '<div class="secondary">' +
                formatter(d.data.percent, this.config.percentageFormat) +
                '%</div>'
              : ''
          }
          `
        }
      },
      getItemOffset: function ($el, d) {
        // let parentRect = this.root.node().getBoundingClientRect()
        let parentRect = $el.closest('.chart').getBoundingClientRect()

        let center = this._arc.centroid(d)
        // console.log(parentRect,center)
        return {
          x: parentRect.x + parentRect.width / 2 + center[0],
          y: parentRect.y + parentRect.height / 2 + center[1],
        }
      },
    },
  }

  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper]
  formatter(value, format) {
    let match = this.matchFormatString(format || '')
    let numberFormat = match && match[2]
    let thouDel = this.config.thousandsDelimiter
    let decDel = this.config.decimalDelimiter
    return formatNumber(value, numberFormat, thouDel, decDel)
  }
  beforeHelpers() {
    // this.content = this.root.selectAll('.content').data([null])
    // this.content = this.content.merge(this.content.enter().append('g').attr('class','content'))
  }
  getRadiuses() {
    let radius
    if (this.config.shape === 'half')
      radius = Math.min((0.75 * this.size.width) / 2, (0.92 * this.size.height) / 1.35)
    else radius = Math.min((0.75 * this.size.width) / 2, (0.85 * this.size.height) / 2)
    if (
      (this.config.showLabels &&
        (this.config.showLabelCategories || this.config.labelValue !== 'none')) ||
      this.config.showLabelIcons
    )
      radius = radius - 30
    let innerRadius
    if (this.config.mode === 'donut') {
      if (this.config.donutThickness === 'thick') innerRadius = 0.5 * radius
      else if (this.config.donutThickness === 'thin') innerRadius = 0.91 * radius
      else innerRadius = 0.7 * radius
    } else {
      innerRadius = 0.01 * radius
    }
    return { radius, innerRadius }
  }
  drawCenterValue(value) {
    const self = this

    let { radius, innerRadius } = this.getRadiuses()

    let fontSize = 28
    let centerValue = d3_select(this.$root)
      .selectAll('.center-value')
      .data((this.config.mode === 'donut' && this.config.centerValue) || value ? [null] : [])
    let centerValueEnter = centerValue
      .enter()
      .append('text')
      .attr('class', 'center-value')
      .style('opacity', 0)

    centerValue.exit().transition().style('opacity', 0).remove()
    centerValue = centerValue.merge(centerValueEnter)
    centerValue
      .attr(
        'transform',
        `translate(${this.size.width / 2},${
          this.size.height / 2 + (this.config.shape === 'half' ? radius * 0.25 : 0)
        })`
      )
      .attr('fill', 'currentColor')
      .attr('font-size', fontSize)
      .attr('font-family', 'santander-microtext-embedded')
      .attr('text-anchor', 'middle')
      // .attr('dominant-baseline','central')
      // .attr('alignment-baseline','central')
      .attr('y', '0.36em')

    // this.formatValue(this.data.reduce((sum,d)=>sum+d.value,0))

    // centerValueEnter.text(this.formatValue(centerValueValue)

    let centerValueValue =
      value || value === 0
        ? value
        : this.config.centerValue === true
        ? this.data.reduce((sum, dd) => sum + dd.data.value, 0)
        : this.config.centerValue || null
    let isPair =
      centerValueValue &&
      (centerValueValue.hasOwnProperty('left') || centerValueValue.hasOwnProperty('right'))

    centerValueEnter.text('')
    let targetValue
    if (!isPair) {
      if (!isNaN(Number(centerValueValue))) {
        centerValue
          .transition()
          .duration(this._transitionDuration)
          .textTween(function () {
            const i = d3_interpolate(this._current || 0, Number(centerValueValue))
            return function (t) {
              this._current = i(t)
              return self.valueWithUnit(this._current)
            }
          })
          .style('opacity', 1)
        targetValue = self.valueWithUnit(centerValueValue)
      } else {
        // if(centerValueValue.indexOf('<')>=0){
        //   let fo = centerValue.selectAll('foreignObject').data([null],(d)=>d)
        //   let foEnter = fo.enter().append('foreignObject')
        //     .attr('class','foreignObject')
        //   foEnter.append('xhtml:div')
        //     .attr('class','html-container')
        //   fo = fo.merge(foEnter)
        //   fo.select('div').html(centerValueValue)
        //   targetValue = null
        // } else {
        centerValue.text(centerValueValue).transition().style('opacity', 1)
        targetValue = centerValueValue
        // }
      }
    } else {
      centerValue
        .html(
          `<tspan font-size="1em" font-weight="bold" x="-${
            centerValueValue.left.length * 0.3
          }em" dx="-6" y="0.36em">${
            centerValueValue.left
          }</tspan><tspan font-size="0.7em" x="0" y="0.36em"> / </tspan><tspan font-size="0.7em" x="${
            centerValueValue.right.length * 0.3
          }em" dx="6" y="0.36em">${centerValueValue.right}</tspan>`
        )
        .transition()
        .style('opacity', 1)
      targetValue = null
    }

    if (centerValue.node() && targetValue) {
      let tmp = centerValue.text()
      centerValue.text(targetValue)
      let l = centerValue.node().getComputedTextLength()
      while (l > 1.8 * innerRadius && fontSize > 10) {
        fontSize = fontSize - 1
        centerValue.attr('font-size', fontSize)
        l = centerValue.node().getComputedTextLength()
      }
      centerValue.text(tmp)
    }
  }
  draw() {
    let self = this
    function findNeighborArc(i, data0, data1, key, backward) {
      var d
      if (!backward) {
        /* eslint-disable no-cond-assign */
        return (d = findPreceding(i, data0, data1, key))
          ? { startAngle: d.endAngle, endAngle: d.endAngle } /* eslint-disable no-cond-assign */
          : // : (d = findFollowing(i, data0, data1, key)) ? {startAngle: d.startAngle, endAngle: d.startAngle}
          // : null;
          self.config.shape === 'half'
          ? { startAngle: -Math.PI / 2, endAngle: -Math.PI / 2 }
          : { startAngle: 0, endAngle: 0 }
      } else {
        return (d = findFollowing(i, data0, data1, key))
          ? { startAngle: d.startAngle, endAngle: d.startAngle } /* eslint-disable no-cond-assign */
          : // :(d = findPreceding(i, data0, data1, key)) ? {startAngle: d.endAngle, endAngle: d.endAngle} /* eslint-disable no-cond-assign */
            // : null;
            { startAngle: 2 * Math.PI, endAngle: 2 * Math.PI }
      }
    }

    // Find the element in data0 that joins the highest preceding element in data1.
    function findPreceding(i, data0, data1, key) {
      var m = data0.length
      while (--i >= 0) {
        var k = key(data1[i])
        for (var j = 0; j < m; ++j) {
          if (key(data0[j]) === k) return data0[j]
        }
      }
    }

    // Find the element in data0 that joins the lowest following element in data1.
    function findFollowing(i, data0, data1, key) {
      var n = data1.length,
        m = data0.length
      while (++i < n) {
        var k = key(data1[i])
        for (var j = 0; j < m; ++j) {
          if (key(data0[j]) === k) return data0[j]
        }
      }
    }

    // Store the displayed angles in _current.
    // Then, interpolate from _current to the new angles.
    // During the transition, _current is updated in-place by d3_interpolate.
    function arcTween(a) {
      var i = d3_interpolate(this._current, { ...a, innerRadius, radius })
      this._current = i(0)

      return function (t) {
        let arc = d3_arc().innerRadius(i(t).innerRadius).outerRadius(i(t).radius)
        return arc(i(t))
      }
    }

    function fillTween(d) {
      const fill = self.fill(d)
      if (fill?.includes('url'))
        return function (t) {
          return fill
        }
      else {
        const currentFill = d3_select(this).attr('fill')
        var i = d3_interpolateRgb(currentFill, fill)
        return function (t) {
          return i(t)
        }
      }
    }

    function key(d) {
      return d && d.data && d.data.id
    }
    let { radius, innerRadius } = this.getRadiuses()
    let arc = d3_arc().innerRadius(innerRadius).outerRadius(radius)
    this._arc = arc

    //(0.85 * this.size.height) / 1.35
    if (this.config.shape === 'half')
      this.content.attr(
        'transform',
        `translate(${this.size.width / 2},${this.size.height / 2 + radius / 2})`
      )
    else this.content.attr('transform', `translate(${this.size.width / 2},${this.size.height / 2})`)

    this.itemsEnter.append('path')

    var data0 = this._prevData || this.data
    var data1 = this.data

    this.itemsEnter
      .select('path')
      .attr('fill', d => this.fill(d.data))
      .each(function (d, i) {
        if (data0 === data1)
          this._current = {
            startAngle: self.config.shape === 'half' ? -Math.PI / 2 : 0,
            endAngle: self.config.shape === 'half' ? -Math.PI / 2 : 0,
          }
        else this._current = findNeighborArc(i, data0, data1, key) || d
      })
      .attr('d', function () {
        return arc(this._current)
      })

    this.itemsEnterTrans
      .delay(0)
      .select('path')
      .attrTween('fill', fillTween)
      .attrTween('d', arcTween)

    this.itemsTrans.select('path').attrTween('fill', fillTween).attrTween('d', arcTween)

    this.itemsExitTrans
      .style('opacity', 0)
      .select('path')
      .attrTween('d', function (d, i) {
        return arcTween.call(this, findNeighborArc(i, data1, data0, key, true) || d)
      })

    // this.itemsExit.datum(function(d, i) { return findNeighborArc(i, data1, data0, key) || d; })
    // this.itemsExitTrans.select('path').attrTween("d", arcTween)

    // CENTRAL VALUE
    //BASURAS HARDCODEADAS...
    let centerHeight = 20
    let centerMargin = 35
    let TOP_BOTTOM_OFFSET = 6

    let fontSize = 28
    this.drawCenterValue()

    // OTHER CENTER TEXTS
    let centerSizes = { width: innerRadius * 1.6, height: innerRadius }

    let svgTexts = [] // [{pos: 'top', text: this.config.centerTopText},{pos: 'bottom', text: this.config.centerBottomText}]
    let divTexts = [] //
    if (this.config.centerTopText && this.config.centerTopText.indexOf('<') >= 0)
      divTexts.push({ pos: 'top', text: this.config.centerTopText })
    else svgTexts.push({ pos: 'top', text: this.config.centerTopText })

    if (this.config.centerBottomText && this.config.centerBottomText.indexOf('<') >= 0)
      divTexts.push({ pos: 'bottom', text: this.config.centerBottomText })
    else svgTexts.push({ pos: 'bottom', text: this.config.centerBottomText })

    let centerTextsDivs = d3_select(this.$root)
      .selectAll('.center-text-div')
      .data(this.config.mode === 'donut' ? divTexts : [])
    let centerTextsSvgs = d3_select(this.$root)
      .selectAll('.center-text-svg')
      .data(this.config.mode === 'donut' ? svgTexts : [])

    // IF DIV TEXTS (NOT GOOD for exporting, but good for advanced devs)
    let centerTextsDivsEnter = centerTextsDivs
      .enter()
      .append('g')
      .attr('class', d => 'center-text-div ' + d.pos)
      .style('opacity', 0)

    centerTextsDivsEnter
      .append('foreignObject')
      .attr('class', 'foreignObject')
      .append('xhtml:div')
      .attr('class', 'html-container')
    centerTextsDivs.exit().transition().style('opacity', 0).remove()
    centerTextsDivs = centerTextsDivs.merge(centerTextsDivsEnter)

    centerTextsDivs.attr(
      'transform',
      d =>
        `translate(${this.size.width / 2 - centerSizes.width / 2},${
          this.size.height / 2 + (d.pos === 'top' ? -centerSizes.height : 0)
        })`
    )

    centerTextsDivs
      .select('.foreignObject')
      .attr('width', centerSizes.width)
      .attr('height', centerSizes.height)

    centerTextsDivs
      .select('.foreignObject')
      .select('.html-container')
      // .style('transform',(d,i)=>`translate(-50%,${i===0?'-100%':'0'})`)
      .style('display', 'flex')
      .style('flex-direction', 'column')
      .style('height', '100%')
      .style('justify-content', d => (d.pos === 'top' ? 'flex-end' : 'flex-start'))
      .style('align-items', 'center')
      .style('text-align', 'center')
      .html(d => d.text)

    centerTextsDivs
      .select('.foreignObject')
      .select('.html-container')
      .transition()
      .style('padding-bottom', d => (this.config.centerValue && d.pos === 'top' ? '20px' : '0px'))
      .style('padding-top', d => (this.config.centerValue && d.pos === 'bottom' ? '20px' : '0px'))

    centerTextsDivs.transition().style('opacity', 1)

    // IF SVG TEXTS (GOOD for exporting)
    let centerTextsSvgsEnter = centerTextsSvgs
      .enter()
      .append('g')
      .attr('class', d => 'center-text-svg ' + d.pos)
      .style('opacity', 0)

    centerTextsSvgsEnter
      .append('g')
      .attr('class', 'foreignObject')
      .append('text')
      .attr('class', 'html-container')

    centerTextsSvgs.exit().transition().style('opacity', 0).remove()
    centerTextsSvgs = centerTextsSvgs.merge(centerTextsSvgsEnter)

    fontSize = 16
    centerTextsSvgs.attr(
      'transform',
      d =>
        `translate(${this.size.width / 2},${
          this.size.height / 2 +
          (this.config.shape === 'half' ? radius * 0.25 : 0) +
          (d.pos === 'bottom' ? centerMargin : centerHeight - centerMargin)
        })`
    )
    centerTextsSvgs
      .select('.foreignObject')
      .select('.html-container')
      .attr('fill', 'currentColor')
      .attr('font-size', fontSize)
      .attr('font-family', 'santander-microtext-embedded')
      .attr('text-anchor', 'middle')
      // .attr('dy','0.36em')
      // .attr('dominant-baseline',(d)=>d.pos==='top'?'central':'central')
      .text(d => d.text)
      .call(wrapSvgText, radius)
      .attr('transform', function (d) {
        let height = d.pos === 'top' ? this.getBBox().height : 0
        return `translate(0,${TOP_BOTTOM_OFFSET - height})`
      })
    centerTextsSvgs.transition().style('opacity', 1)

    // LABELS && Icons
    let getIcon = d => {
      return (
        !!this.config.showLabelIcons &&
        this.config.ItemsHelper.colorDict &&
        this.config.ItemsHelper.colorDict[d.id]?.icon?.icon &&
        this.config.ItemsHelper.colorDict[d.id]?.icon
      )
    }
    //Check if has icon or text in order to append label
    /*
    const showLabel = d => {
      if (this.config.showLabels || this.config.showLabelIcons) {
        return this.config.showLabelIcons ? !!getIcon(d) : true
      }
      return false
    }
    */
    const showLabel = d => {
      return (
        (this.config.showLabels &&
          (this.config.showLabelCategories || this.config.labelValue !== 'none')) ||
        (this.config.showLabelIcons && !!getIcon(d))
      )
    }

    // this.itemsEnter.selectAll('.label').data([null]).enter().append('g').attr('class','label')
    let labels = this.items
      .merge(this.itemsEnter)
      .selectAll('.label')
      .data(
        d => (showLabel(d) ? [{ ...d, customId: d.id + this.config.shape, label: d.id }] : []),
        dd => dd.customId
      )
    let labelsEnter = labels.enter().append('g').attr('class', 'label')
    labels.exit().remove()
    this.itemsExitTrans.select('.label').attr('opacity', 0)
    let r0 = radius * 0.05,
      dr = radius * 0.2,
      dh = radius * 0.1

    labelsEnter
      .append('path')
      .attr('stroke', 'currentColor')
      .attr('fill', 'none')
      .style('opacity', 0)

    //Icons
    const ICON_HEIGHT = 23
    const ICON_WIDTH = 23
    const ICON_OFFSET = 5

    /*
    labelsEnter
      .append('text')
      .attr('fill', 'currentColor')
      .attr('alignment-baseline', 'middle')
      .attr('font-size', '14')
      .attr('font-family', 'santander-microtext-embedded')
    */

    //const labelsMerge = labels.merge(labelsEnter)

    let getLabelAngle = d => {
      //
      if (this.config.labelOffsets?.[d.id])
        return (
          d.startAngle +
          (d.endAngle - d.startAngle) * (this.config.labelOffsets[d.id] / 100) -
          Math.PI / 2
        )
      else return d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
    }

    let outerCentroid = d => {
      let angle = getLabelAngle(d)
      return [Math.cos(angle) * radius, Math.sin(angle) * radius]
    }
    const isLabelOffsetCustomized = d => {
      let labelOffset = this.config.labelOffsets?.[d.id]
      return !!labelOffset || labelOffset === 0
    }
    //NO esta del todo bien... algunas veces hace "elbows rectos" muy feos...
    let labelPath = d => {
      // let center = this._arc.centroid(d)
      let angle = getLabelAngle(d)
      let center = outerCentroid(d)
      let sign = center[0] > 0 ? 1 : -1
      let start, joint, end
      if (isLabelOffsetCustomized(d)) {
        start = [center[0] + Math.cos(angle) * r0, center[1] + Math.sin(angle) * r0]
        joint = [center[0] + Math.cos(angle) * dr, center[1] + Math.sin(angle) * dr]
        end = [joint[0] + 15 * sign, joint[1]]
      } else {
        start = [center[0] + Math.cos(angle) * r0, center[1] + Math.sin(angle) * r0]
        joint = [(d.y * Math.cos(angle)) / Math.sin(angle), d.y]
        end = [d.x - 3 * sign, d.y]
        //un poco cutre hacerlo con sort...
        let jointRange = [start[0], end[0]]
        jointRange.sort((a, b) => a - b)
        joint[0] = Math.min(Math.max(joint[0], jointRange[0]), jointRange[1])
      }

      return `M ${start} L ${joint} L ${[end]}`
    }
    let labelTextAnchor = d => {
      // let center = this._arc.centroid(d)
      let center = outerCentroid(d)
      let sign = center[0] > 0 ? 1 : -1
      return sign < 0 ? 'end' : 'start'
    }

    let labelTransform = function (d, offset) {
      // let center = this._arc.centroid(d)
      let angle = getLabelAngle(d)
      let center = outerCentroid(d)
      let sign = center[0] > 0 ? 1 : -1
      let translate = []
      //Check if datum contains d. It means that forces placed it
      if (isLabelOffsetCustomized(d)) {
        translate = [
          center[0] + Math.cos(angle) * dr + (3 + 15) * sign,
          center[1] + Math.sin(angle) * dr,
        ]
      } else {
        translate = [d.x, d.y]
      }
      /*
      else {
        let angle = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
        let joint = [
          (d.y * Math.cos(angle)) / Math.sin(angle),
          isLabelOffsetCustomized(d) ? d.cy : d.y,
        ]
        translate = [joint[0] + sign * dh * 1.2, joint[1]]
      }
      */
      //Check offset (in order to place icons && texts together) and apply to translate
      if (!!offset) {
        translate[0] += offset[0] * sign
        translate[1] += offset[1]
      }
      return `translate(${translate})`
    }
    function pathTween(a) {
      var i = d3_interpolate(this._current, a)
      this._current = i(0)
      return function (t) {
        return labelPath(i(t))
      }
    }
    function transTween(a, offset) {
      var i = d3_interpolate(this._current, a)
      this._current = i(0)
      return function (t) {
        return labelTransform(i(t), offset)
      }
    }

    //const labelsMerge = labels.merge(labelsEnter)
    const customScale = d => {
      let center = outerCentroid(d)
      let angle = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
      let sign = center[0] > 0 ? 1 : -1
      // let start = [center[0]+Math.cos(angle)*r0,center[1]+Math.sin(angle)*r0]
      let joint = [center[0] + Math.cos(angle) * dr, center[1] + Math.sin(angle) * dr]
      let end = [joint[0] + sign * dh * 1.2, joint[1]]
      return end
    }

    labelsEnter.style('opacity', 0)
    labelsEnter
      .select('text')
      .attr('text-anchor', labelTextAnchor)
      .attr('transform', labelTransform)

    labels = labels.merge(labelsEnter)

    let labelsText = labels.selectAll('text').data(
      d => (this.config.showLabels ? [d] : []),
      d => d.id
    )
    labelsText.exit().remove()
    const labelsTextEnter = labelsText
      .enter()
      .append('text')
      .attr('fill', 'currentColor')
      .html(d => d.id)
      .attr('alignment-baseline', 'middle')
      .attr('font-family', 'santander-microtext-embedded')

    labelsText = labelsText.merge(labelsTextEnter)
    labelsText.attr(
      'font-size',
      this._parent.accessibilityOptions?.[ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize]
        ? '18px'
        : '14px'
    )

    const getLabelValue = d => {
      switch (this.config.labelValue) {
        case 'abs':
          return this.valueWithUnit(d.data.value)
        case 'percent':
          return this.formatter(d.data.percent, this.config.percentageFormat) + '%'
        default:
          return null
      }
    }
    const getLabelHtml = d => {
      let labelCategory = this.config.showLabelCategories ? d.label : null
      let labelValue = getLabelValue(d)
      if (labelCategory)
        return (
          `<tspan alignment-baseline="middle">${d.label}${labelValue ? ': ' : ''}</tspan>` +
          (labelValue
            ? `<tspan alignment-baseline="middle" style="opacity: 1; font-size: 14px; font-weight: bold;">${labelValue}</tspan>`
            : '')
        )
      else if (labelValue) {
        return `<tspan alignment-baseline="middle" style="opacity: 1; font-size: 14px; font-weight: bold;">${labelValue}</tspan>`
      }
      return ''
    }
    labels.select('text').html(d => getLabelHtml(d))
    labels.select('text').each(function (d) {
      let center = outerCentroid(d)
      // let r = self.getRadiuses().radius
      let remaining = self.size.width / 2 - Math.abs(center[0])
      let maxWidth = remaining - 60
      d3_select(this)
        .call(wrapSvgText, maxWidth)
        .selectAll('tspan')
        .each(function () {
          let str = getLabelValue(d)
          d3_select(this).attr('alignment-baseline', 'middle')
          if (str && this.innerHTML.indexOf(str) >= 0) {
            this.innerHTML = this.innerHTML.replace(str, ' ')
            d3_select(this.parentNode)
              .append('tspan')
              .text(str)
              .attr('font-weight', 'bold')
              .attr('alignment-baseline', 'middle')
            // this.innerHTML = this.innerHTML.replace(str,`<tspan alignment-baseline="middle" style="opacity: 1; font-size: 14px; font-weight: bold;">${str}</tspan>`)
          }
        })
    })

    //Icons
    let translate = [0, -ICON_HEIGHT / 2 - 2]

    let iconGroup = labels
      .selectAll('.icon')
      .data(d => (getIcon(d)?.icon ? [{ id: d.id, ...getIcon(d) }] : []))
    iconGroup.exit().remove()
    let iconGroupEnter = iconGroup.enter().append('g').attr('class', 'icon')
    //iconGroupEnter.style('opacity', 0).transition().duration(500).style('opacity', 1)
    iconGroup = iconGroup.merge(iconGroupEnter)
    iconGroup.attr('transform', `translate(${translate[0]}, ${translate[1]})`)
    iconGroup.html(d => (d ? d.icon.svg : null))
    let iconGroupSvg = iconGroup.select('svg')
    //Hay q hardcodear nuevos ids en los defs y en los use para hacer unicas las modificaciones
    iconGroupSvg
      .select('defs')
      .select('path')
      .attr('id', function (d) {
        return this.getAttribute('id') ? this.getAttribute('id') + `donut-chart-icon-${d.id}` : null
      })
    iconGroupSvg.select('use').attr('xlink:href', function (d) {
      return this.getAttribute('xlink:href')
        ? this.getAttribute('xlink:href') + `donut-chart-icon-${d.id}`
        : null
    })
    iconGroupSvg.selectAll('path, polygon').attr('fill', function () {
      let d = d3_select(this.parentNode).datum()
      return d && d.color && !d.icon.disableColorModifier ? d.color : this.getAttribute('fill')
    })
    iconGroupSvg.attr('height', `${ICON_HEIGHT}px`).attr('width', `${ICON_WIDTH}px`)
    iconGroupSvg.select('use').attr('fill', function (d) {
      return d && d.color && !d.icon.disableColorModifier ? d.color : this.getAttribute('fill')
    })

    let labelsTrans = labels.transition().duration(1000)
    //labelsTrans.select('text').attrTween('transform', transTween)
    pieChartLabels(labels, customScale, this.getRadiuses().radius)

    labelsTrans.style('opacity', 1)
    labelsTrans
      .select('path')
      .attrTween('d', pathTween)
      .transition()
      .duration(500)
      .style('opacity', 1)
    labelsTrans
      .select('text')
      .attr('text-anchor', labelTextAnchor)
      // .attr('transform', labelTransform)
      .attrTween('transform', function (d) {
        return transTween.call(this, d, getIcon(d)?.icon ? [ICON_WIDTH + 4, 0] : null)
      })
    labelsTrans
      .select('.icon')
      // .attr('transform', labelTransform)
      .attrTween('transform', function (d) {
        let center = outerCentroid(d)
        let sign = center[0] > 0 ? 1 : -1
        return transTween.call(this, d, [sign === -1 ? ICON_WIDTH : 0, (ICON_HEIGHT / 2) * -1 - 2])
      })

    // we must reset selection (NO ME GUSTA, lo quito)
    // this.selection = this.data.filter(d => this.selection.find(dd => dd.id === d.id) !== undefined)
    // this._updateSelection()
  }

  setSelectedFunc(sel, d) {
    if (this.config.mode !== 'donut') return
    let { radius, innerRadius } = this.getRadiuses()

    // let offset = (radius - innerRadius)*0.75
    let offset
    if (this.config.donutThickness === 'thin') offset = (radius - innerRadius) * 0.75
    else if (this.config.donutThickness === 'medium') offset = (radius - innerRadius) * 0.15
    else offset = (radius - innerRadius) * 0.1

    radius = radius + offset
    innerRadius = innerRadius - offset
    let arc = d3_arc().innerRadius(innerRadius).outerRadius(radius)
    // let glow = sel.selectAll('.glow').data(this.config.donutThickness==='thin'?[d]:[])
    let glow = sel.selectAll('.glow').data([d])
    glow.exit().remove()
    glow = glow.merge(glow.enter().append('path').attr('class', 'glow'))
    glow
      .attr('d', arc)
      .attr('fill', d => this.color(d))
      .attr('opacity', 0)
      .transition()
      .duration(200)
      .attr('opacity', 0.25)

    if (this.config.selectedItemCenterValue) {
      this.drawCenterValue(d.value)
    }
  }
  unsetSelectedFunc(sel) {
    if (this.config.mode !== 'donut') return
    let glow = sel.selectAll('.glow').data([])
    glow.exit().remove()
    if ((this.config.selectedItemCenterValue && !this.selection) || this.selection.length === 0) {
      this.drawCenterValue()
    }
  }
}
